/* eslint-disable no-unused-vars */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseHref } from "service/api";
import { Transaction } from "types/transaction";
import { UsdEur } from "types/usd-eur";

export type FullBalancesResponseDto = {
  details: BalanceResponseDto[];
  summary: BalanceResponseDto[];
  availableEurSum: number;
  availableUsdSum: number;
};

export type ChildDetailsDto = {
  clientId: string;
  login: string;
  details: BalanceResponseDto[];
};

export type BalanceResponseDto = {
  last?: boolean;
  address?: string;
  asset: Coin;
  total: number;
  totalEur: number;
  totalUsd: number;
  available: number;
  availableUsd: number;
  availableEur: number;
  usdRate: number;
  eurRate: number;
};

export enum Coin {
  BTC = "btc",
  ETH = "eth",
  USDT = "usdt",
  USDC = "usdc",
  DAI = "dai",
  TRX = "trx",
  USDT_TRC20 = "usdt_trc20",
  USDC_TRC20 = "usdc_trc20",
  XRP = "xrp",
  BSC = "bsc",
  SOLANA = "solana",
}

export type Balances = {
  btc: string;
  eth: number;
  usdt: number;
  usd: string;
  eur: string;
  usdc: number;
  dai: number;
  usdc_trc20: number;
  usdt_trc20: number;
  trx: number;
  xrp: number;
};

type BalancesResponse = {
  newArchBalances: FullBalancesResponseDto;
  available_balances: Balances;
  balances: Balances;
  rates: {
    btc: UsdEur;
    eth: UsdEur;
    usdt: UsdEur;
    xrp: UsdEur;
    usdc: UsdEur;
    dai: UsdEur;
  };
};

type TransactionsResponse = {
  transactions: Transaction[];
  total: number;
};

type AddressResponse = {
  address: string;
};

type TransactionsParams = {
  type: string;
  [key: string]: any;
};

export const financeApi = createApi({
  reducerPath: "financeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseHref}/finance`,
    credentials: "include",
  }),
  tagTypes: ["transactionsAll"],
  endpoints: (builder) => ({
    balances: builder.query<BalancesResponse, void>({
      query: () => `balances`,
    }),
    balancesForMainUserClient: builder.query<BalancesResponse, string>({
      query: (clientId) =>
        `balances-for-main-user-client?clientId=${encodeURIComponent(
          clientId
        )}`,
    }),
    transactions: builder.query<TransactionsResponse, TransactionsParams>({
      query: ({ type, ...query }) =>
        `transactions/${type}${
          query
            ? "?" +
              new URLSearchParams(
                Object.fromEntries(
                  Object.entries(query).filter(([_, v]) => !!v)
                )
              ).toString()
            : ""
        }`,
      providesTags: ["transactionsAll"],
      keepUnusedDataFor: 0,
    }),
    address: builder.query<
      AddressResponse,
      | "usdt"
      | "eth"
      | "btc"
      | "usdc"
      | "trx"
      | "usdt_trc20"
      | "usdc_trc20"
      | "dai"
      | "xrp"
    >({
      query: (address) => `address/${address.toLowerCase()}`,
    }),
  }),
});

export const {
  useBalancesQuery,
  useTransactionsQuery,
  useAddressQuery,
  useLazyBalancesForMainUserClientQuery,
} = financeApi;
